import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDeleteLeft,
  faPhone,
  faRotateLeft,
} from "@fortawesome/free-solid-svg-icons";

const keys = [
  ["1", "2", "3"],
  ["4", "5", "6"],
  ["7", "8", "9"],
  ["*", "0", "#"],
];

export const extractPhoneDigits = (value = "") => {
  const rawValue = `${value}`.trim();
  const withoutDisplayedPrefix = rawValue.startsWith("+1")
    ? rawValue.slice(2)
    : rawValue;
  const digitsOnly = withoutDisplayedPrefix.replace(/\D/g, "");
  const localDigits =
    digitsOnly.length > 10 && digitsOnly.startsWith("1")
      ? digitsOnly.slice(1)
      : digitsOnly;

  return localDigits.slice(0, 10);
};

export const formatPhoneDigits = (value = "") => {
  const digits = extractPhoneDigits(value);

  return digits ? `+1${digits}` : "";
};

export const toDialablePhoneNumber = (value = "") => {
  const digits = extractPhoneDigits(value);

  if (!digits) {
    return "";
  }

  return `+1${digits}`;
};

export default function Dialpad({
  value,
  outboundNumber,
  onChange,
  onAppendDigit,
  onBackspace,
  onClear,
  onStartCall,
  isBusy = false,
  isStartDisabled = false,
  callError = "",
  startButtonLabel = "Call",
}) {
  return (
    <section className="dialer-dialpad dialpad">
      <div className="dialer-dialpad-header">
        <h2 className="dialer-section-title">Dialpad</h2>
      </div>

      <div className="dialer-input-shell">
        <label
          htmlFor="dialer-destination-input"
          className="dialer-input-label"
        >
          Destination
        </label>
        <div className="dialer-input-row">
          <input
            id="dialer-destination-input"
            type="text"
            className="dialer-input"
            inputMode="tel"
            autoComplete="tel"
            value={formatPhoneDigits(value)}
            placeholder="5551234567"
            disabled={isBusy}
            onChange={(event) => onChange(extractPhoneDigits(event.target.value))}
            onKeyDown={(event) => {
              if (event.key === "Enter" && !isStartDisabled) {
                onStartCall();
              }
            }}
          />
        </div>
        <div className="dialer-input-meta">
          <div className="dialer-input-helper">
            US numbers only. +1 is added automatically. From {outboundNumber}
          </div>
        </div>
      </div>

      <div className="dialer-keypad-grid dialpad-grid">
        {keys.flat().map((key, index) =>
          key ? (
            <button
              key={key}
              type="button"
              className="dialer-keypad-button keypad-btn"
              disabled={isBusy}
              onClick={() => {
                if (/^\d$/.test(key)) {
                  onAppendDigit(key);
                }
              }}
            >
              {key}
            </button>
          ) : (
            <div key={`dialpad-empty-${index}`} aria-hidden="true" />
          ),
        )}
      </div>

      <div className="dialer-action-panel">
        <div className="dialer-keypad-actions action-row">
          <div className="action-slot">
            <button
              type="button"
              onClick={onClear}
              className="dialer-action-circle"
              aria-label="Clear"
              disabled={isBusy}
            >
              <span className="dialer-action-icon" aria-hidden="true">
                <FontAwesomeIcon icon={faRotateLeft} />
              </span>
            </button>
          </div>
          <div className="action-slot">
            <button
              type="button"
              onClick={onStartCall}
              disabled={isStartDisabled}
              className="dialer-action-circle is-primary"
              aria-label="Call"
            >
              <span className="dialer-action-icon" aria-hidden="true">
                <FontAwesomeIcon icon={faPhone} />
              </span>
              <span className="dialer-action-text">{startButtonLabel}</span>
            </button>
          </div>
          <div className="action-slot">
            <button
              type="button"
              onClick={onBackspace}
              className="dialer-action-circle"
              aria-label="Backspace"
              disabled={isBusy}
            >
              <span className="dialer-action-icon" aria-hidden="true">
                <FontAwesomeIcon icon={faDeleteLeft} />
              </span>
            </button>
          </div>
        </div>
      </div>

      {callError ? (
        <div className="dialer-inline-feedback is-error">{callError}</div>
      ) : null}
    </section>
  );
}
